<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { i18n } from '@/i18n'

const t = i18n.global.t

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  existingProjectNames: {
    type: Array as () => string[],
    default: () => [],
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  create: [name: string, description: string]
}>()

const projectName = ref('')
const projectDescription = ref('')

const projectNameValid = computed(() => {
  return !props.existingProjectNames.some((name) => name === projectName.value)
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      projectName.value = ''
      projectDescription.value = ''
    }
  },
)

function handleCancel() {
  emit('update:modelValue', false)
}

function handleCreate() {
  if (projectName.value && projectNameValid.value) {
    emit('create', projectName.value, projectDescription.value)
    emit('update:modelValue', false)
  }
}
</script>

<template>
  <div
    class="absolute w-full h-full p-20 bg-black/70 z-20 font-alexandria flex flex-col items-center justify-start pt-96"
    v-if="modelValue"
  >
    <div v-if="modelValue" class="bg-white rounded-2xl flex flex-col p-8">
      <h1 class="text-3xl">{{ t('createNewProject') }}</h1>
      <p class="text-md pt-3">{{ t('createNewProjectDetails') }}</p>
      <h3 class="text-xl pt-10">{{ t('createNewProjectName') }}</h3>
      <input type="text" v-model="projectName" class="border rounded-lg border-grey p-3 mt-4" />
      <h3 class="text-xl pt-10">{{ t('createNewProjectDescription') }}</h3>
      <textarea
        v-model="projectDescription"
        class="border rounded-lg border-grey p-3 mt-4"
      ></textarea>
      <h3 class="text-xl pt-10">{{ t('createNewProjectPurpose') }}</h3>
      <div class="flex flex-row pt-4">
        <button class="px-4 py-2 rounded-lg border cursor-pointer">
          {{ t('createNewProjectPurposePrivate') }}
        </button>
        <button class="px-4 py-2 rounded-lg border ml-4 cursor-pointer">
          {{ t('createNewProjectPurposeClassroom') }}
        </button>
      </div>
      <div class="flex flex-row pt-4 place-self-end">
        <button
          class="px-4 py-2 rounded-lg bg-green text-white cursor-pointer"
          @pointerdown="handleCancel"
        >
          {{ t('cancel') }}
        </button>
        <button
          class="px-4 py-2 rounded-lg ml-4 cursor-pointer"
          :disabled="!projectName"
          :class="{
            'bg-green text-white': projectNameValid,
            'bg-grey text-black': !projectNameValid,
          }"
          @pointerdown="handleCreate"
        >
          {{ t('createNewProjectCreateButton') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
